/**
 * Ordini del cliente su una commessa.
 *
 * Dove il cliente emette un ordine d'acquisto, la fattura deve citarne il
 * numero e restare dentro l'importo autorizzato. Qui si registra l'ordine —
 * anche partendo dal PDF già allegato, che viene letto per proporre i campi —
 * e si vede in ogni momento quanto ne resta.
 */
import { useEffect, useState } from 'react';
import { ordiniApi } from '../api';
import Modal from './Modal';
import CampoImporto from './CampoImporto';

const fmt = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';

const vuoto = { numero: '', data: '', importo: '', scadenza: '', riferimento: '', note: '', allegato_id: '', stato: 'attivo' };

export default function PannelloOrdini({ commessaId, budget, onCambiato }) {
  const [dati, setDati] = useState(null);
  const [modal, setModal] = useState(null);      // { mode, id }
  const [form, setForm] = useState(vuoto);
  const [lettura, setLettura] = useState(null);  // avvertenze della lettura automatica
  const [salvando, setSalvando] = useState(false);

  const carica = () => ordiniApi.perCommessa(commessaId).then(setDati);
  useEffect(() => { carica(); }, [commessaId]);

  if (!dati) return <div className="text-gray-400 text-sm">Caricamento…</div>;

  const apri = (o) => {
    setLettura(null);
    setForm(o ? { ...o, data: o.data?.slice(0, 10) || '', scadenza: o.scadenza?.slice(0, 10) || '',
                  importo: o.importo ?? '', allegato_id: o.allegato_id || '' }
              : { ...vuoto });
    setModal({ mode: o ? 'edit' : 'new', id: o?.id });
  };

  // Da un documento già allegato: i campi vengono proposti, mai salvati da soli.
  const daAllegato = async (a) => {
    setLettura(null);
    setForm({ ...vuoto, allegato_id: a.id });
    setModal({ mode: 'new' });
    try {
      const r = await ordiniApi.leggiAllegato(a.id);
      setForm(f => ({ ...f, numero: r.numero || '', data: r.data || '',
                      importo: r.importo ?? '', riferimento: r.riferimento || '' }));
      setLettura({ nome: a.nome_file, avvertenze: r.avvertenze || [] });
    } catch (e) { setLettura({ nome: a.nome_file, avvertenze: [String(e)] }); }
  };

  const salva = async () => {
    setSalvando(true);
    try {
      if (modal.mode === 'new') await ordiniApi.create(commessaId, form);
      else await ordiniApi.update(modal.id, form);
      setModal(null); await carica(); onCambiato?.();
    } catch (e) { alert(e); } finally { setSalvando(false); }
  };

  const elimina = async (o) => {
    if (!confirm(`Eliminare l'ordine ${o.numero}?`)) return;
    try { await ordiniApi.delete(o.id); await carica(); onCambiato?.(); }
    catch (e) { alert(e); }
  };

  const f = (k) => ({ value: form[k] ?? '', onChange: e => setForm(p => ({ ...p, [k]: e.target.value })) });
  const scoperto = budget > 0 && dati.totale > 0 ? +(budget - dati.totale).toFixed(2) : 0;

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-start gap-4">
        <p className="text-xs text-gray-500 max-w-2xl">
          Dove il cliente emette un ordine d'acquisto, la fattura deve citarne il numero e
          restare nell'importo autorizzato. Registrandolo qui, l'approvazione di un SAL e il
          riepilogo delle fatture avvisano quando qualcosa non torna.
        </p>
        <button className="btn-primary shrink-0" onClick={() => apri(null)}>+ Registra ordine</button>
      </div>

      {/* Documenti già caricati che sembrano ordini */}
      {dati.allegati_candidati?.length > 0 && (
        <div className="card bg-brand-50/50 border-brand-200 py-2.5">
          <div className="text-xs text-gray-600 mb-1.5">
            Documenti allegati non ancora registrati come ordine:
          </div>
          <div className="flex flex-wrap gap-1.5">
            {dati.allegati_candidati.map(a => (
              <button key={a.id} onClick={() => daAllegato(a)}
                      title={a.ha_testo ? 'Leggi il documento e precompila' : 'Il documento non ha testo leggibile: campi da inserire a mano'}
                      className="btn-sm btn bg-white border border-brand-300 text-brand-700 hover:bg-brand-50 max-w-[320px] truncate">
                {a.categoria === 'ordine' ? '📄' : '📎'} {a.nome_file}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Capienza complessiva */}
      {dati.ha_ordini && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="card text-center py-3">
            <div className="text-xs text-gray-500">Ordinato dal cliente</div>
            <div className="text-lg font-bold text-gray-900">{fmt(dati.totale)}</div>
          </div>
          <div className="card text-center py-3">
            <div className="text-xs text-gray-500">Fatturato sull'ordine</div>
            <div className="text-lg font-bold text-brand-600">{fmt(dati.fatturato)}</div>
          </div>
          <div className="card text-center py-3">
            <div className="text-xs text-gray-500">In coda</div>
            <div className={`text-lg font-bold ${dati.in_coda > 0 ? 'text-amber-600' : 'text-gray-300'}`}>{fmt(dati.in_coda)}</div>
          </div>
          <div className="card text-center py-3">
            <div className="text-xs text-gray-500">Ancora ordinabile</div>
            <div className={`text-lg font-bold ${dati.residuo < -0.01 ? 'text-red-600' : 'text-green-600'}`}>{fmt(dati.residuo)}</div>
          </div>
        </div>
      )}

      {/* Scostamenti da sapere prima di fatturare */}
      {dati.ha_ordini && scoperto > 0.01 && (
        <div className="card bg-amber-50/70 border-amber-200 text-sm text-amber-800 py-2.5">
          L'ordine copre {fmt(dati.totale)} a fronte di un budget di commessa di {fmt(budget)}:
          restano <strong>{fmt(scoperto)}</strong> senza ordine. Se il lavoro li prevede, chiedi
          al cliente un'integrazione prima di fatturarli.
        </div>
      )}
      {dati.ha_ordini && dati.fuori_ordine.righe > 0 && (
        <div className="card bg-amber-50/70 border-amber-200 text-sm text-amber-800 py-2.5">
          {dati.fuori_ordine.righe} fatture di questa commessa non citano alcun ordine
          ({fmt(dati.fuori_ordine.fatturato + dati.fuori_ordine.in_coda)}). Aprile e indica
          l'ordine di riferimento, così il conteggio torna.
        </div>
      )}

      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr className="text-xs text-gray-500 text-left">
              <th className="px-4 py-3">Numero</th>
              <th className="px-4 py-3">Data</th>
              <th className="px-4 py-3 text-right">Importo</th>
              <th className="px-4 py-3 text-right">Fatturato</th>
              <th className="px-4 py-3 text-right">In coda</th>
              <th className="px-4 py-3 text-right">Residuo</th>
              <th className="px-4 py-3">Validità</th>
              <th className="px-4 py-3">Rif.</th>
              <th className="px-4 py-3">Stato</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {!dati.ha_ordini && (
              <tr><td colSpan={10} className="px-4 py-6 text-center text-gray-400">
                Nessun ordine registrato: la fatturazione resta libera, controllata solo dal budget.
              </td></tr>
            )}
            {dati.ordini.map(o => (
              <tr key={o.id} className={`border-b last:border-0 hover:bg-gray-50 ${o.stato !== 'attivo' ? 'opacity-60' : ''}`}>
                <td className="px-4 py-3 font-mono text-gray-800">
                  {o.numero}
                  {o.allegato_nome && (
                    <div className="text-[11px] text-gray-400 truncate max-w-[200px]" title={o.allegato_nome}>
                      📄 {o.allegato_nome}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-500">{fmtDate(o.data)}</td>
                <td className="px-4 py-3 text-right font-medium">{o.importo ? fmt(o.importo) : '—'}</td>
                <td className="px-4 py-3 text-right text-brand-700">{fmt(o.fatturato)}</td>
                <td className="px-4 py-3 text-right text-amber-700">{fmt(o.in_coda)}</td>
                <td className={`px-4 py-3 text-right font-semibold ${o.residuo === null ? 'text-gray-400' : o.residuo < -0.01 ? 'text-red-600' : 'text-green-700'}`}>
                  {o.residuo === null ? '—' : fmt(o.residuo)}
                </td>
                <td className="px-4 py-3 text-xs">
                  {o.scadenza
                    ? <span className={o.scaduto ? 'text-red-600 font-medium' : 'text-gray-500'}>
                        {o.scaduto ? 'scaduto il ' : 'fino al '}{fmtDate(o.scadenza)}
                      </span>
                    : <span className="text-gray-400">—</span>}
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">{o.riferimento || '—'}</td>
                <td className="px-4 py-3 text-xs">
                  <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${
                    o.stato === 'attivo' ? 'bg-green-100 text-green-700'
                    : o.stato === 'chiuso' ? 'bg-gray-100 text-gray-600' : 'bg-red-100 text-red-700'}`}>
                    {o.stato}
                  </span>
                </td>
                <td className="px-4 py-3 flex gap-1 justify-end">
                  <button className="btn-secondary btn-sm" onClick={() => apri(o)}>Modifica</button>
                  <button className="btn-danger btn-sm" onClick={() => elimina(o)}>Elimina</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal title={modal.mode === 'new' ? 'Registra ordine del cliente' : 'Modifica ordine'}
               onClose={() => setModal(null)}>
          {lettura && (
            <div className="bg-brand-50/60 border border-brand-200 rounded-lg p-2.5 mb-4 text-xs text-gray-700">
              <div className="font-medium mb-1">Letto da {lettura.nome}</div>
              <ul className="list-disc pl-4 space-y-0.5 text-gray-600">
                {lettura.avvertenze.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Numero ordine *</label>
              <input className="input" placeholder="es. 4503133658" {...f('numero')} />
            </div>
            <div><label className="label">Data ordine</label><input className="input" type="date" {...f('data')} /></div>
            <div>
              <label className="label">Importo autorizzato (€)</label>
              <CampoImporto className="input" value={form.importo} onChange={v => setForm(p => ({ ...p, importo: v }))} />
              <p className="text-xs text-gray-400 mt-0.5">Imponibile. Lascialo vuoto se l'ordine non indica un importo.</p>
            </div>
            <div>
              <label className="label">Valido fino al</label>
              <input className="input" type="date" {...f('scadenza')} />
            </div>
            <div>
              <label className="label">Riferimenti</label>
              <input className="input" placeholder="CIG, CUP, posizione, centro di costo" {...f('riferimento')} />
            </div>
            <div>
              <label className="label">Stato</label>
              <select className="input" value={form.stato} onChange={e => setForm(p => ({ ...p, stato: e.target.value }))}>
                <option value="attivo">Attivo</option>
                <option value="chiuso">Chiuso</option>
                <option value="annullato">Annullato</option>
              </select>
              <p className="text-xs text-gray-400 mt-0.5">Solo gli ordini attivi danno capienza.</p>
            </div>
            <div className="col-span-2"><label className="label">Note</label><textarea className="input" rows={2} {...f('note')} /></div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button className="btn-secondary" onClick={() => setModal(null)}>Annulla</button>
            <button className="btn-primary" onClick={salva} disabled={salvando}>{salvando ? 'Salvo…' : 'Salva'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
